import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';

function LogViewer() {
    const [testResult, setTestResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        testDatabaseConnection();
    }, []);

    const testDatabaseConnection = async () => {
        setLoading(true);
        setError(null);
        setTestResult(null);
        try {
            // The API files are in api/ directory, so the path should be /api/test-db.php
            // But axios baseURL already includes the base path, so we just need the relative path
            const apiUrl = '/api/test-db.php';
            
            console.log('Testing API URL:', apiUrl);
            console.log('Axios baseURL:', axios.defaults.baseURL);
            const response = await axios.get(apiUrl);
            
            // Check if response is HTML (server not running PHP)
            if (typeof response.data === 'string' && (response.data.trim().startsWith('<!') || response.data.includes('<html'))) {
                setError('Server returned HTML instead of JSON. PHP is not running or .htaccess is not configured correctly.');
                setTestResult({
                    status: 'error',
                    connection: {
                        success: false,
                        error: 'PHP files are not being executed. The server is returning React\'s index.html instead of running the PHP script. Check your .htaccess configuration.'
                    },
                    html_response: true
                });
                return;
            }
            
            setTestResult(response.data);
        } catch (err) {
            console.error('Error testing database:', err);
            
            // If we get a response, try to use it
            if (err.response) {
                // If response has data, use it
                if (err.response.data) {
                    setTestResult(err.response.data);
                } else {
                    setError(`HTTP ${err.response.status}: ${err.response.statusText}`);
                    setTestResult({
                        status: 'error',
                        connection: {
                            success: false,
                            error: `HTTP ${err.response.status}: ${err.response.statusText}. The PHP file might not be executing. Check if PHP is enabled on your server.`
                        }
                    });
                }
            } else if (err.request) {
                // Request was made but no response
                setError('No response from server. The API endpoint might not be accessible.');
                setTestResult({
                    status: 'error',
                    connection: {
                        success: false,
                        error: 'No response from server. Check if the API endpoint is accessible and PHP is running.'
                    }
                });
            } else {
                // Something else happened
                setError(err.message);
                setTestResult({
                    status: 'error',
                    connection: {
                        success: false,
                        error: err.message
                    }
                });
            }
            
            // If we got a 404, provide helpful message
            if (err.response && err.response.status === 404) {
                setTestResult({
                    status: 'error',
                    connection: {
                        success: false,
                        error: '404 Not Found - The API file does not exist at the expected path. Make sure the api/ directory is in the correct location on the server.'
                    },
                    suggestion: 'Check that api/test-db.php exists in your server directory'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return '#6c757d';
        switch (status.toLowerCase()) {
            case 'success':
                return '#28a745';
            case 'error':
                return '#dc3545';
            case 'testing':
                return '#ffc107';
            default:
                return '#6c757d';
        }
    };

    const getStatusIcon = (success) => {
        return success ? '✓' : '✗';
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: '20px' }}>Database Connection Test</h1>
            
            <button 
                onClick={testDatabaseConnection}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginBottom: '20px'
                }}
            >
                {loading ? 'Testing...' : 'Test Again'}
            </button>

            {loading && <p>Loading...</p>}

            {error && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    color: '#721c24'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {testResult && (
                <div>
                    {/* Status */}
                    <div style={{
                        padding: '15px',
                        backgroundColor: getStatusColor(testResult.status) + '20',
                        border: `2px solid ${getStatusColor(testResult.status)}`,
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}>
                        <h2 style={{ margin: 0, color: getStatusColor(testResult.status) }}>
                            Status: {(testResult.status || 'unknown').toUpperCase()}
                        </h2>
                    </div>

                    {/* Environment Variables */}
                    <div style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}>
                        <h3>Environment Variables</h3>
                        <p>
                            <strong>Env Loaded:</strong> {testResult.env_loaded ? '✓ Yes' : '✗ No'}
                        </p>
                        {testResult.env_path && (
                            <p><strong>Env Path:</strong> {testResult.env_path}</p>
                        )}
                        {testResult.env_warning && (
                            <p style={{ color: '#ffc107' }}>
                                <strong>Warning:</strong> {testResult.env_warning}
                            </p>
                        )}
                        {testResult.env_error && (
                            <p style={{ color: '#dc3545' }}>
                                <strong>Env Error:</strong> {testResult.env_error}
                            </p>
                        )}
                        {testResult.env_vars && (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#e9ecef' }}>
                                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #dee2e6' }}>Variable</th>
                                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #dee2e6' }}>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(testResult.env_vars).map(([key, value]) => (
                                        <tr key={key}>
                                            <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{key}</td>
                                            <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                                {value === 'NOT SET' ? (
                                                    <span style={{ color: '#dc3545' }}>NOT SET</span>
                                                ) : (
                                                    value
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Connection Test */}
                    {testResult.connection && (
                        <div style={{
                            padding: '15px',
                            backgroundColor: testResult.connection?.success ? '#d4edda' : '#f8d7da',
                            border: `1px solid ${testResult.connection?.success ? '#c3e6cb' : '#f5c6cb'}`,
                            borderRadius: '4px',
                            marginBottom: '20px'
                        }}>
                            <h3>
                                {getStatusIcon(testResult.connection?.success)} Database Connection
                            </h3>
                            {testResult.connection?.success ? (
                                <p style={{ color: '#155724' }}>Connection successful!</p>
                            ) : (
                                <div>
                                    <p style={{ color: '#721c24' }}>
                                        <strong>Error:</strong> {testResult.connection?.error || 'Unknown error'}
                                    </p>
                                    {testResult.connection?.file && (
                                        <p style={{ color: '#721c24', fontSize: '12px' }}>
                                            File: {testResult.connection.file}:{testResult.connection.line}
                                        </p>
                                    )}
                                    {testResult.connection?.trace && (
                                        <details style={{ marginTop: '10px' }}>
                                            <summary style={{ cursor: 'pointer', color: '#721c24' }}>Stack Trace</summary>
                                            <pre style={{ 
                                                marginTop: '10px', 
                                                padding: '10px', 
                                                backgroundColor: '#fff', 
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                overflow: 'auto'
                                            }}>
                                                {testResult.connection.trace.join('\n')}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Query Test */}
                    {testResult.query_test && (
                        <div style={{
                            padding: '15px',
                            backgroundColor: testResult.query_test.success ? '#d4edda' : '#fff3cd',
                            border: `1px solid ${testResult.query_test.success ? '#c3e6cb' : '#ffeaa7'}`,
                            borderRadius: '4px',
                            marginBottom: '20px'
                        }}>
                            <h3>
                                {getStatusIcon(testResult.query_test.success)} Query Test
                            </h3>
                            {testResult.query_test.success ? (
                                <p style={{ color: '#155724' }}>
                                    Found {testResult.query_test.count} records in `rekeningen` table
                                </p>
                            ) : (
                                <p style={{ color: '#856404' }}>
                                    <strong>Error:</strong> {testResult.query_test.error}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Full JSON (for debugging) */}
                    <details style={{ marginTop: '20px' }}>
                        <summary style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                            View Full JSON Response
                        </summary>
                        <pre style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            overflow: 'auto',
                            marginTop: '10px'
                        }}>
                            {JSON.stringify(testResult, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </div>
    );
}

export default LogViewer;

